import React from 'react';
import { Box, Typography, Tooltip, Chip } from "@mui/material";
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import CategoryItem from './CategoryItem';
import { colors } from '../../utils/themeUtils';

// Custom styled tooltip to match the app's tooltip style
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: 'rgba(250, 250, 252, 0.98)',
    padding: '10px 14px',
    fontSize: '0.85rem',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    color: '#333',
    '&.MuiTooltip-tooltipPlacementTop': {
      marginBottom: '6px'
    }
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: 'rgba(250, 250, 252, 0.98)'
  }
});

/**
 * Component to render the Quick Overview section in the dashboard
 */
const QuickOverview = ({
  timeframe,
  sortOption,
  view,
  overallStats,
  categoryStats,
  getTimeframeDays,
  formatCurrency,
  formatImpact,
  isCategoryHighlighted,
  handleCategoryClick,
  currencySymbol,
  numSelectedBusinesses
}) => {
  // Helper function to get category impact values for the tooltip
  const getCategoryImpactValues = (categoryName) => {
    const category = categoryStats.topCategories.find(
      cat => cat.name.toLowerCase() === categoryName.toLowerCase()
    );
    
    if (!category) return { positive: 0, negative: 0 };
    
    // Determine which impact values to use based on the view
    let positiveImpact, negativeImpact;
    if (view === "Sales") {
      // Use the pre-calculated summed revenue impact from categoryStats
      positiveImpact = category.positiveRevenueImpact || 0;
      negativeImpact = Math.abs(category.negativeRevenueImpact || 0);
    } else {
      // Use the average rating impact from categoryStats
      positiveImpact = category.averagePositiveRatingImpact || 0;
      negativeImpact = Math.abs(category.averageNegativeRatingImpact || 0);
    }

    // Removed the flawed single-business multiplier logic
    
    return { 
      positive: positiveImpact, 
      negative: negativeImpact 
    };
  };
  
  // Helper function to render tooltip content
  const getTooltipContent = (categoryName) => {
    const { positive, negative } = getCategoryImpactValues(categoryName);
    
    return (
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2">
            Positive: <span style={{ color: colors.positiveGreen, fontWeight: 'bold' }}>
              +{view === "Sales" 
                ? formatCurrency(positive)
                : formatImpact(positive)}
            </span>
          </Typography>
          <Typography variant="body2">
            Negative: <span style={{ color: colors.negativeRed, fontWeight: 'bold' }}>
              -{view === "Sales" 
                ? formatCurrency(negative)
                : formatImpact(negative)}
            </span>
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'auto'
    }}>
      {/* Dynamic Category Filter Links - Changed to single line list */} 
      {categoryStats.topCategories && categoryStats.topCategories.length > 0 && (
        // Single Typography component to hold the text and interactive category names
        <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 1 }}> {/* Added mt for spacing */} 
          <Box component="span" sx={{ color: '#000000DE', whiteSpace: 'nowrap' }}>
            Filters: 
          </Box>
          {/* Map categories directly inside Typography, using fragments and adding commas */} 
          {categoryStats.topCategories.map((category, index) => (
            <React.Fragment key={category.name}>
              <StyledTooltip 
                title={getTooltipContent(category.name)}
                arrow
                placement="top"
              >
                <Typography
                  component="span" // Use span for inline behavior within the parent Typography
                  onClick={() => handleCategoryClick(category.name)}
                  sx={theme => ({
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    color: isCategoryHighlighted(category.name) ? theme.palette.primary.main : theme.palette.text.primary,
                    // Removed padding/background for a cleaner text look, rely on hover/color change
                    transition: 'color 0.2s ease-in-out',
                    '&:hover': {
                      color: theme.palette.primary.dark,
                    },
                    // Add back data attributes if needed
                    dataFilterTerm: "category", 
                    dataCategoryBox: "true" 
                  })}
                >
                  {/* Add space before the category name for separation */} 
                  {' ' + category.name.charAt(0).toUpperCase() + category.name.slice(1)}
                </Typography>
              </StyledTooltip>
              {/* Add comma and space if not the last item */} 
              {index < categoryStats.topCategories.length - 1 ? ',' : ''}
            </React.Fragment>
          ))}
        </Typography>
      )}
    </Box>
  );
};

export default QuickOverview; 